import type { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Workbench/Flask/Tooltip";
import { GHS_PICTOGRAM_NAMES } from "@/utils/lens";
import type { SubstanceSupplyFlags } from "@/utils/types/substance";

export type SupplyModalKind = "inventory" | "supplier" | "safety";

type RowIconBtnProps = {
  className?: string;
  onClick?: () => void;
  title?: string;
  dim?: boolean;
  children: ReactNode;
};

function RowIconBtn({ className, onClick, title, dim, children }: RowIconBtnProps) {
  return (
    <button
      type="button"
      onClick={dim ? undefined : onClick}
      title={title}
      className={cn("row-icon-btn", className, dim && "dim")}
    >
      {children}
    </button>
  );
}

type RowSupplyIconsProps = {
  compoundId: string;
  flags: SubstanceSupplyFlags | null;
  loading: boolean;
  onOpen: (kind: SupplyModalKind, id: string) => void;
};

// Pure function of (flags, loading). Tooltip text comes from the supply-flags
// batch payload (inventoryTop / marketCheapest / safetySummary) — no mock
// lookups. Loading state runs only while the batch is in-flight on the very
// first flask render; after that the React Query cache is warm.
//
// Spec: §5 / §8.6 of 2026-05-11-current-flask-server-data-design.
export function RowSupplyIcons({
  compoundId,
  flags,
  loading,
  onOpen,
}: RowSupplyIconsProps) {
  const inv = flags?.inventoryTop ?? null;
  const mkt = flags?.marketCheapest ?? null;
  const saf = flags?.safetySummary ?? null;
  const hasHaz = saf !== null && (saf.signal !== null || saf.pictograms.length > 0);
  const sevClass =
    saf?.signal === "Danger"
      ? "sev-danger"
      : saf?.signal === "Warning"
      ? "sev-warning"
      : "sev-mild";

  return (
    <div className={cn("supply-icons-cell", loading && "is-loading")}>
      {/* Inventory */}
      {inv ? (
        <Tooltip
          label={`In stock: ${inv.catalogNo}`}
          sublabel={`${inv.sizeLabel} · ${inv.quantity} on hand`}
        >
          <RowIconBtn className="inv" onClick={() => onOpen("inventory", compoundId)}>
            <Icon name="box" size={13} />
          </RowIconBtn>
        </Tooltip>
      ) : (
        <RowIconBtn dim title={loading ? "Loading…" : "Not in inventory"}>
          <Icon name="box" size={13} />
        </RowIconBtn>
      )}

      {/* Supplier */}
      {mkt ? (
        <Tooltip
          label={`Cheapest: ${mkt.productNo}`}
          sublabel={`${mkt.packagingLabel} · ${
            mkt.pricePerGram != null ? mkt.pricePerGram.toFixed(2) : "—"
          } ${mkt.currency}/g · ${mkt.supplier}`}
        >
          <RowIconBtn className="sup" onClick={() => onOpen("supplier", compoundId)}>
            <Icon name="truck" size={13} />
          </RowIconBtn>
        </Tooltip>
      ) : (
        <RowIconBtn dim title={loading ? "Loading…" : "No suppliers"}>
          <Icon name="truck" size={13} />
        </RowIconBtn>
      )}

      {/* Safety */}
      {saf === null ? (
        // No safety record at all — disabled, matches the dim treatment
        // used by the inventory/supplier icons when their data is missing.
        // Distinct from the "record exists but no hazards" branch below,
        // which still opens the safety modal (food-grade ingredients have
        // valid practical-info content even without GHS pictograms).
        <RowIconBtn dim title={loading ? "Loading…" : "No Safety Information"}>
          <Icon name="shield" size={13} />
        </RowIconBtn>
      ) : hasHaz ? (
        <Tooltip
          label={`${saf.signal ?? "Hazards"} · ${saf.pictograms.length} hazard${
            saf.pictograms.length > 1 ? "s" : ""
          }`}
          sublabel={saf.pictograms
            .map((p) => `${p} ${GHS_PICTOGRAM_NAMES[p] ?? ""}`)
            .join(" · ")}
        >
          <RowIconBtn
            className={sevClass}
            onClick={() => onOpen("safety", compoundId)}
          >
            <Icon name={saf.signal === "Danger" ? "alert" : "shield"} size={13} />
          </RowIconBtn>
        </Tooltip>
      ) : (
        <Tooltip
          label={loading ? "Loading safety…" : "Food-grade · no GHS pictograms"}
          sublabel="Handle as standard food ingredient"
        >
          <RowIconBtn className="sev-mild" onClick={() => onOpen("safety", compoundId)}>
            <Icon name="shield" size={13} />
          </RowIconBtn>
        </Tooltip>
      )}
    </div>
  );
}
